import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useLang } from "./i18n";
import "./ImpactCalculator.css";

const fmt = (n: number) => Math.round(n).toLocaleString();

const COPY = {
  en: {
    label: "Your Impact",
    title: "See the difference one pickup makes.",
    sub: "Slide to match what you've got lying around — and watch your impact add up. ♻️",
    sliders: ["Electronics & e-waste", "Bags of clothing", "Boxes of household goods"],
    co2: "🌍 lbs CO₂ saved", water: "💧 gallons of water saved", items: "🎁 items rehomed",
    equiv1: "🌳 That's like ", equivTree: (n: number) => `${n} tree${n > 1 ? "s" : ""}`, equiv2: " working for a whole year!",
    btn: "Make it real — Schedule a Pickup",
    note: "*Friendly estimates, just to show your impact.",
  },
  es: {
    label: "Tu Impacto",
    title: "Mira la diferencia que hace una recogida.",
    sub: "Mueve las barras según lo que tengas por ahí — y mira cómo suma tu impacto. ♻️",
    sliders: ["Electrónicos y e-waste", "Bolsas de ropa", "Cajas de artículos del hogar"],
    co2: "🌍 lbs de CO₂ ahorradas", water: "💧 galones de agua ahorrados", items: "🎁 artículos con nuevo hogar",
    equiv1: "🌳 ¡Es como ", equivTree: (n: number) => `${n} árbol${n > 1 ? "es" : ""}`, equiv2: " trabajando un año entero!",
    btn: "Hazlo realidad — Agenda una Recogida",
    note: "*Estimados amistosos, solo para mostrar tu impacto.",
  },
};

const Slider: React.FC<{ icon: string; label: string; value: number; onChange: (n: number) => void }> = ({
  icon, label, value, onChange,
}) => (
  <div className="ee-calc-row">
    <div className="ee-calc-row-top">
      <span className="ee-calc-row-label"><span className="ee-calc-ico">{icon}</span>{label}</span>
      <span className="ee-calc-row-val">{value}</span>
    </div>
    <input
      type="range" min={0} max={12} value={value}
      onChange={(e) => onChange(Number(e.target.value))}
      className="ee-calc-slider"
      aria-label={label}
    />
  </div>
);

const ImpactCalculator: React.FC = () => {
  const { lang } = useLang();
  const L = COPY[lang];
  const [elec, setElec] = useState(2);
  const [clothes, setClothes] = useState(1);
  const [house, setHouse] = useState(1);

  const co2 = elec * 25 + clothes * 30 + house * 15;
  const water = elec * 8 + clothes * 500 + house * 40;
  const items = elec * 1 + clothes * 12 + house * 8;
  const trees = Math.max(1, Math.round(co2 / 48));

  return (
    <section id="impact" className="ee-section ee-calc">
      <div className="ee-container">
        <div className="ee-section-header" style={{ textAlign: "center", maxWidth: 620, margin: "0 auto 56px" }}>
          <div className="ee-section-label" style={{ justifyContent: "center" }}>{L.label}</div>
          <h2 className="ee-section-title">{L.title}</h2>
          <p className="ee-section-sub" style={{ margin: "0 auto" }}>{L.sub}</p>
        </div>

        <div className="ee-calc-grid">
          <div className="ee-calc-inputs">
            <Slider icon="💻" label={L.sliders[0]} value={elec} onChange={setElec} />
            <Slider icon="👕" label={L.sliders[1]} value={clothes} onChange={setClothes} />
            <Slider icon="📦" label={L.sliders[2]} value={house} onChange={setHouse} />
          </div>

          <div className="ee-calc-results">
            <div className="ee-calc-stat"><span className="ee-calc-num">{fmt(co2)}</span><span className="ee-calc-lbl">{L.co2}</span></div>
            <div className="ee-calc-stat"><span className="ee-calc-num">{fmt(water)}</span><span className="ee-calc-lbl">{L.water}</span></div>
            <div className="ee-calc-stat"><span className="ee-calc-num">{fmt(items)}</span><span className="ee-calc-lbl">{L.items}</span></div>
            <div className="ee-calc-equiv">{L.equiv1}<strong>{L.equivTree(trees)}</strong>{L.equiv2}</div>
            <Link to="/pickup" className="ee-btn ee-btn-green ee-calc-btn">{L.btn}</Link>
            <p className="ee-calc-note">{L.note}</p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ImpactCalculator;
